/**
 * UploadScanModal
 *
 * Vulnerability Management scan-upload modal — uses the shared
 * FormModal primitive so the chrome (overlay, header, escape /
 * backdrop close, focus trap, error rendering, submit lifecycle)
 * matches the Athena dashboard's Create New Project modal verbatim.
 *
 * The previous flow navigated to /vulnerability-management/projects/
 * {projectUuid}/scans/new which rendered NewAnalysis as a full
 * PageLayout-wrapped form — a blocking page transition for what is
 * conceptually an inline action. This modal preserves the form
 * fields (componentName + version + isLatest + sbomFile) but keeps
 * the user on the dashboard.
 */
import React from 'react';
import FormModal from '../../components/ui/FormModal/FormModal';
import { useVulnerabilityManagement } from '../../contexts/VulnerabilityManagementContext';

const VALID_EXTENSIONS = [
  '.json', '.xml',
  '.spdx', '.spdx.json', '.spdx.xml',
  '.spdx.yaml', '.spdx.rdf',
];

const validateExtension = (file) => {
  if (!file) return 'SBOM file is required';
  const name = file.name?.toLowerCase() ?? '';
  if (!VALID_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return 'Invalid file format. Supported: CycloneDX (.json, .xml), SPDX (.spdx, .spdx.json, .spdx.xml, .spdx.yaml, .spdx.rdf)';
  }
  return null;
};

const FIELDS = [
  {
    name: 'componentName',
    type: 'text',
    label: 'Component Name',
    placeholder: 'e.g., Backend',
    required: true,
    autoFocus: true,
    maxLength: 255,
    validate: (value) =>
      (value || '').trim().length === 0 ? 'Component name is required' : null,
  },
  {
    name: 'version',
    type: 'text',
    label: 'Version',
    placeholder: 'e.g., 1.0.0',
    required: true,
    maxLength: 100,
    validate: (value) =>
      (value || '').trim().length === 0 ? 'Version is required' : null,
  },
  {
    name: 'isLatest',
    type: 'checkbox',
    label: 'Track this version',
    defaultValue: true,
  },
  {
    name: 'sbomFile',
    type: 'file',
    label: 'SBOM File',
    required: true,
    accept: '.json,.xml,.spdx,.spdx.json,.spdx.xml,.spdx.yaml,.spdx.rdf',
    helpText: 'Supported: CycloneDX (.json, .xml), SPDX 2.x (.spdx, .spdx.json, .spdx.xml)',
    validate: validateExtension,
  },
];

const UploadScanModal = ({ projectUuid, onClose, onUploaded }) => {
  const { uploadScan } = useVulnerabilityManagement();

  return (
    <FormModal
      title="Upload SBOM"
      fields={FIELDS}
      submitLabel="Upload & Analyze"
      submittingLabel="Uploading..."
      maxWidth={560}
      onSubmit={async (values) => {
        await uploadScan(
          projectUuid,
          values.componentName.trim(),
          values.version.trim(),
          values.sbomFile,
          values.isLatest,
        );
        if (onUploaded) onUploaded();
      }}
      onClose={onClose}
    />
  );
};

export default UploadScanModal;
